import React, { useState, useEffect, useCallback } from 'react';
import { useConfirm } from '../../components/common/ConfirmDialog';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Tooltip,
    List,
    ListItem,
    ListItemText,
    Chip,
    Snackbar,
    Alert,
    Divider,
    Pagination,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Menu
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Timer as TimerIcon,
    Stop as StopIcon,
    PlayArrow as PlayArrowIcon,
    ContentCopy as ContentCopyIcon,
    FiberManualRecord as FiberManualRecordIcon,
    Http as HttpIcon,
    Search as SearchIcon,
    FilterList as FilterListIcon,
    Sort as SortIcon,
    MoreVert as MoreVertIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DashboardBreadcrumbs from "../../components/DashboardBreadcrumbs";
import { Button as OpusButton } from "../../components/common/opus";
import { Grid } from "@mui/material";
import galleryService from '../../services/gallery.service';
import { AE_palette } from '../../style/theme';
import { debounce } from 'lodash';

// --- Session Dialog Component (Kept separate for cleaner main component) ---
const SessionDialog = ({ open, onClose, gallery }) => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchSessions = async () => {
        if (!gallery) return;
        setLoading(true);
        try {
            const data = await galleryService.getSessions(gallery._id);
            setSessions(data);
        } catch (error) {
            console.error('Error fetching sessions', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open && gallery) {
            fetchSessions();
        }
    }, [open, gallery]);

    const handleStartSession = async () => {
        try {
            await galleryService.startSession(gallery._id, { createdBy: 'dashboard' });
            fetchSessions();
        } catch (error) {
            console.error('Error starting session', error);
        }
    };

    const handleStopSession = async (sessionId) => {
        try {
            await galleryService.stopSession(sessionId);
            fetchSessions();
        } catch (error) {
            console.error('Error stopping session', error);
        }
    };

    const activeSession = sessions.find(s => s.status === 'active');

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Sessions for {gallery?.name}</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                        Current Status: {activeSession ? <span style={{ color: 'green' }}>Active</span> : <span style={{ color: 'grey' }}>Inactive</span>}
                    </Typography>
                    <Button
                        variant="contained"
                        color={activeSession ? "error" : "primary"}
                        startIcon={activeSession ? <StopIcon /> : <PlayArrowIcon />}
                        onClick={activeSession ? () => handleStopSession(activeSession._id) : handleStartSession}
                    >
                        {activeSession ? "Stop Current Session" : "Start New Session"}
                    </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle1" gutterBottom>Session History</Typography>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>
                ) : (
                    <List dense>
                        {sessions.map((session) => (
                            <ListItem key={session.session_id || session._id} divider>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <FiberManualRecordIcon fontSize="small" color={session.status === 'active' ? 'success' : 'disabled'} />
                                            <Typography variant="body1">{session.status.toUpperCase()}</Typography>
                                        </Box>
                                    }
                                    secondary={
                                        <>
                                            <Typography component="span" variant="body2" display="block">
                                                ID: {session.session_id} | Start: {new Date(session.createdAt).toLocaleString()}
                                            </Typography>
                                            <Typography component="span" variant="caption" display="block" color="textSecondary">
                                                User: {session.user_name}
                                            </Typography>
                                        </>
                                    }
                                />
                            </ListItem>
                        ))}
                        {sessions.length === 0 && <Typography align="center" sx={{ py: 2, color: 'text.secondary' }}>No session history.</Typography>}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

// --- Main Gallery Management Component ---
const GalleryManagement = () => {
    const navigate = useNavigate();
    const [galleries, setGalleries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
    const [filters, setFilters] = useState({ search: '', status: '', sortBy: 'createdAt', order: 'desc' });

    // Dialog States
    const [open, setOpen] = useState(false); // Create/Edit Modal
    const [formData, setFormData] = useState({ name: '', description: '', targetUrl: '', shortCode: '', status: 'draft' });
    const [editingId, setEditingId] = useState(null);
    const [errors, setErrors] = useState({});

    // Session Dialog
    const [sessionOpen, setSessionOpen] = useState(false);
    const [selectedGallery, setSelectedGallery] = useState(null);

    // Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    // Row Menu
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuRowId, setMenuRowId] = useState(null);

    const fetchGalleries = async () => {
        setLoading(true);
        try {
            const data = await galleryService.getGalleries({
                page: pagination.page,
                limit: pagination.limit,
                search: filters.search,
                status: filters.status,
                sortBy: filters.sortBy,
                order: filters.order
            });
            // Handle both new paginated response and potential old array response for backward compat check
            if (data.data) {
                setGalleries(data.data);
                setPagination(prev => ({ ...prev, total: data.total, pages: data.pages }));
            } else {
                setGalleries(data); // Fallback to array if backend not updated (should show warning but works)
            }
        } catch (error) {
            console.error('Error fetching galleries', error);
            setSnackbar({ open: true, message: 'Error loading galleries', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Debounce search
    const debouncedSearch = useCallback(
        debounce((value) => {
            setFilters(prev => ({ ...prev, search: value }));
        }, 500),
        []
    );

    useEffect(() => {
        fetchGalleries();
    }, [pagination.page, filters.search, filters.status, filters.sortBy, filters.order]);

    const handleSearchChange = (e) => {
        debouncedSearch(e.target.value);
    };

    const handleSort = (field) => {
        const isAsc = filters.sortBy === field && filters.order === 'asc';
        setFilters(prev => ({ ...prev, sortBy: field, order: isAsc ? 'desc' : 'asc' }));
    };

    const handleOpen = (gallery = null) => {
        setErrors({});
        if (gallery) {
            setFormData({
                name: gallery.name,
                description: gallery.description || '',
                targetUrl: gallery.targetUrl || '',
                shortCode: gallery.shortCode || '',
                status: gallery.status || 'draft'
            });
            setEditingId(gallery._id);
        } else {
            setFormData({ name: '', description: '', targetUrl: '', shortCode: '', status: 'draft' });
            setEditingId(null);
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditingId(null);
    };

    const validateForm = () => {
        const tempErrors = {};
        if (!formData.name) tempErrors.name = 'Name is required';
        if (formData.shortCode) {
            if (!/^[a-zA-Z0-9_-]+$/.test(formData.shortCode)) tempErrors.shortCode = 'Invalid characters';
            if (formData.shortCode.length < 3) tempErrors.shortCode = 'Too short';
        }
        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            let res;
            if (editingId) {
                res = await galleryService.updateGallery(editingId, formData);
            } else {
                res = await galleryService.createGallery(formData);
            }
            fetchGalleries();
            handleClose();
            setSnackbar({ open: true, message: 'Gallery saved!', severity: 'success' });

            if (!editingId) {
                // Redirect to details on create
                navigate(`/dashboard/galleries/${res._id}`);
            }
        } catch (error) {
            console.error('Error saving gallery', error);
            const msg = error.response?.data?.message || 'Error saving gallery';
            setSnackbar({ open: true, message: msg, severity: 'error' });
        }
    };

    const confirm = useConfirm();

    const handleDelete = async (id) => {
        if (await confirm({ title: "Delete gallery?", message: "Are you sure you want to delete this gallery?", destructive: true })) {
            try {
                await galleryService.deleteGallery(id);
                fetchGalleries();
                setSnackbar({ open: true, message: 'Gallery deleted', severity: 'success' });
            } catch (error) {
                setSnackbar({ open: true, message: 'Error deleting gallery', severity: 'error' });
            }
        }
        handleMenuClose();
    };

    const handleMenuOpen = (event, id) => {
        setAnchorEl(event.currentTarget);
        setMenuRowId(id);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuRowId(null);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setSnackbar({ open: true, message: 'Copied!', severity: 'success' });
    };

    return (
        <Grid container spacing={1} sx={{ height: '100vh' }}>
            <Grid size={12}>
                {/* OPUS-1045: move the action into the breadcrumb's rightAction
                    slot (consistent with other pages) + use the on-system OpusButton
                    create style instead of the off-palette seafoamGreen MUI button. */}
                <DashboardBreadcrumbs
                    rightAction={
                        <OpusButton variant="primary" size="md" icon="plus" onClick={() => handleOpen()}>
                            Create Gallery
                        </OpusButton>
                    }
                />
            </Grid>

            {/* Main Content Area */}
            <Grid size={12} sx={{ height: '100%' }}>
                <Box
                    sx={{
                        display: "flex", flexDirection: 'column',
                        height: "calc(100% - 67px)",
                        width: "100%",
                        p: 2,
                        background: "var(--surface-1)",
                        border: "1px solid var(--border)",
                        borderRadius: "var(--r-lg)",
                    }}
                >
                    {/* Filters Header */}
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <Typography variant="body1" sx={{ mr: 2 }}>
                            {pagination.total || 0} galleries
                        </Typography>

                        <Divider orientation="vertical" variant="middle" flexItem sx={{ mr: 2 }} />

                        <TextField
                            size="small"
                            placeholder="Search..."
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ width: 250 }}
                        />

                        <FormControl size="small" sx={{ minWidth: 150, ml: 2 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={filters.status}
                                label="Status"
                                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                <MenuItem value="published">Published</MenuItem>
                                <MenuItem value="draft">Draft</MenuItem>
                                <MenuItem value="archived">Archived</MenuItem>
                            </Select>
                        </FormControl>

                        <Box sx={{ flexGrow: 1 }} />
                    </Box>

                    {/* Table Area */}
                    <Box sx={{
                        flexGrow: 1,
                        overflow: 'auto',
                        width: '100%',
                        "&::-webkit-scrollbar": {
                            width: "8px !important",
                        },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "rgba(255,255,255,0.3) !important",
                            borderRadius: "4px !important",
                        },
                        "&::-webkit-scrollbar-track": {
                            backgroundColor: "rgba(255,255,255,0.1) !important",
                        }
                    }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => handleSort('name')}>
                                            Name {filters.sortBy === 'name' && <SortIcon fontSize="small" sx={{ transform: filters.order === 'asc' ? 'rotate(180deg)' : 'none' }} />}
                                        </Box>
                                    </TableCell>
                                    <TableCell>Short URL</TableCell>
                                    <TableCell>Created By</TableCell>
                                    <TableCell>Items</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Updated</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : galleries.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 10, color: 'text.secondary' }}>
                                            <Typography variant="h6">No galleries found</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    galleries.map((gallery) => (
                                        <TableRow key={gallery._id} hover>
                                            <TableCell>
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{ color: AE_palette.seafoamGreen, cursor: 'pointer', fontWeight: 'bold' }}
                                                    onClick={() => navigate(`/dashboard/galleries/${gallery._id}`)}
                                                >
                                                    {gallery.name}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">{gallery.description?.substring(0, 50)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                {gallery.shortCode && (
                                                    <Chip
                                                        icon={<ContentCopyIcon fontSize="small" onClick={(e) => { e.stopPropagation(); copyToClipboard(`http://localhost:3005/galleries/s/${gallery.shortCode}`); }} />}
                                                        label={gallery.shortCode}
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => window.open(`http://localhost:3005/galleries/s/${gallery.shortCode}`, '_blank')}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>{gallery.createdBy || 'System'}</TableCell>
                                            <TableCell>{gallery.items?.length || 0}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={gallery.status || 'Draft'}
                                                    size="small"
                                                    color={gallery.status === 'published' ? 'success' : gallery.status === 'archived' ? 'default' : 'warning'}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={new Date(gallery.updatedAt).toLocaleString()}>
                                                    <Typography variant="body2">{new Date(gallery.updatedAt).toLocaleDateString()}</Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton onClick={(e) => handleMenuOpen(e, gallery._id)}>
                                                    <MoreVertIcon />
                                                </IconButton>
                                                <Menu
                                                    anchorEl={anchorEl}
                                                    open={Boolean(anchorEl) && menuRowId === gallery._id}
                                                    onClose={handleMenuClose}
                                                >
                                                    <MenuItem onClick={() => { navigate(`/dashboard/galleries/${gallery._id}`); handleMenuClose(); }}>
                                                        <VisibilityIcon fontSize="small" sx={{ mr: 1 }} /> View Details
                                                    </MenuItem>
                                                    <MenuItem onClick={() => { handleOpen(gallery); handleMenuClose(); }}>
                                                        <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
                                                    </MenuItem>
                                                    <MenuItem onClick={() => { setSelectedGallery(gallery); setSessionOpen(true); handleMenuClose(); }}>
                                                        <TimerIcon fontSize="small" sx={{ mr: 1 }} /> Manage Sessions
                                                    </MenuItem>
                                                    <MenuItem onClick={() => handleDelete(gallery._id)} sx={{ color: 'error.main' }}>
                                                        <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
                                                    </MenuItem>
                                                </Menu>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination
                            count={pagination.pages}
                            page={pagination.page}
                            onChange={(e, v) => setPagination({ ...pagination, page: v })}
                            color="primary"
                        />
                    </Box>
                </Box>
            </Grid>

            {/* Create/Edit Dialog */}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{editingId ? 'Edit Gallery' : 'Create New Gallery'}</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus margin="dense" name="name" label="Gallery Name" fullWidth
                        value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        error={!!errors.name} helperText={errors.name}
                    />
                    <TextField
                        margin="dense" name="description" label="Description" fullWidth multiline rows={3}
                        value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                    <Grid container spacing={2}>
                        <Grid size={6}>
                            <TextField
                                margin="dense" name="targetUrl" label="Target URL" fullWidth
                                value={formData.targetUrl} onChange={(e) => setFormData({ ...formData, targetUrl: e.target.value })}
                            />
                        </Grid>
                        <Grid size={6}>
                            <TextField
                                margin="dense" name="shortCode" label="Short Code" fullWidth
                                value={formData.shortCode} onChange={(e) => setFormData({ ...formData, shortCode: e.target.value })}
                                error={!!errors.shortCode} helperText={errors.shortCode}
                                placeholder="Auto-generated if empty"
                            />
                        </Grid>
                    </Grid>
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={formData.status}
                            label="Status"
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        >
                            <MenuItem value="draft">Draft</MenuItem>
                            <MenuItem value="published">Published</MenuItem>
                            <MenuItem value="archived">Archived</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary">
                        {editingId ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            <SessionDialog open={sessionOpen} onClose={() => setSessionOpen(false)} gallery={selectedGallery} />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Grid>
    );
};

export default GalleryManagement;
